import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { decode } from "jwt-simple";

import type { RootState } from "../../../store/state/rootReducer";
import type { AppDispatch } from "../../../store/state/rootStore";
import ChapterCard from "../Index/Cards/ChapterCard";
import EditChapterModal from "../Index/Modals/EditChapterModal";
import DeleteIndexItemModal from "../Index/Modals/DeleteIndexItemModal";
import {
  DeleteIndexMetadataItem,
  EditIndexMetadataItem,
  SetClipsLength,
  setSelectedText,
} from "../../../store/state/story_authoring/reducer";
import { createClip, updateModalCreateClip, updateCloseModal } from "../../../store/state/story_authoring/actions";
import type { ITime } from "../../../store/state/story_authoring/reducer";
import { secondsToTimecode } from "../TSVideoPlayer/utils";
import ClipModal from "../Modals/ClipModal/ClipModal";

import { getDescription } from "./utils";

interface UrlParams {
  index_id: string;
  story_id: string;
}

interface Item {
  keywords: string;
  lines: any[];
  notes: string;
  synopsis: string;
  time: { end: number | null; start: number | null };
  timecode: string;
  title: string;
}

const Metadata = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { index_id: indexId, story_id: storyId } = useParams<keyof UrlParams>();
  const [isReadOnlyUser, setIsReadOnlyUser] = useState(false);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [showEditChapterModal, setShowEditChapterModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItemPosition, setSelectedItemPosition] = useState<number>();

  const st = useSelector((state: RootState) => state);
  const indexes = useSelector((s: RootState) => s.storyAuthoring.items) || [];
  const story = useSelector((s: RootState) => s.storyAuthoring.story);
  const transcript = useSelector((s: RootState) => s.storyAuthoring.transcriptSlate);
  const index = indexId ? indexes.find((_index) => _index._id === indexId) : indexes.length > 0 ? indexes[0] : null;
  const { metadata = [] } = index ?? {};

  const url = (story ?? {}).archiveURL;
  const clipsLength = st.storyAuthoring.clipsLength;

  useEffect(() => {
    const parsedToken = decode(st.auth.token, null, true);
    const capabilities = "https://theirstory.io/token-capabilities";
    if (parsedToken && parsedToken[capabilities]) {
      setIsReadOnlyUser(parsedToken[capabilities][0]?.type === "ReadTranscript");
    }
  }, []);

  const onEdit = (title: string, description: string, keywords: string, startTime: number) => {
    const metadataItem = {
      title,
      timecode: secondsToTimecode(startTime),
      time: {
        start: startTime,
        end: null,
      } as ITime,
      synopsis: description,
      notes: "",
      keywords,
      lines: [],
    };
    dispatch(EditIndexMetadataItem({ indexId, itemPosition: selectedItemPosition, item: metadataItem }));
    setShowEditChapterModal(false);
  };

  const onDelete = () => {
    dispatch(DeleteIndexMetadataItem({ indexId, itemPosition: selectedItemPosition }));
    setShowDeleteItemModal(false);
  };

  const createClipHandler = (item: Item, i: number) => {
    const start = item.time.start;
    const end = metadata[i + 1] ? metadata[i + 1].time.start : story.duration;
    const description = getDescription(start, end, transcript);

    dispatch(
      updateModalCreateClip({
        clipTitle: item.title,
        clipDesc: description,
        clipUrl: `${url}#t=${st.storyAuthoring.clipStart},${st.storyAuthoring.clipEnd}`,
        clipStart: Number(start),
        clipEnd: Number(end),
        clipDuration: story.duration,
        clipKeywords: item.keywords,
      })
    );
    setShowModal(true);
  };

  async function createClipCall(title, description, start, end, tags) {
    const startPoint = String(start);
    const endPoint = String(end);
    const finalTitle = title === "" ? `Clip ${Number(clipsLength) + 1}` : title;
    dispatch(createClip({ storyId, title: finalTitle, description, clipStart: startPoint, clipEnd: endPoint, tags }));
    dispatch(updateCloseModal);
    dispatch(SetClipsLength(clipsLength + 1));
    dispatch(setSelectedText(""));
    setShowModal(false);
  }

  return (
    <div>
      {[...metadata].map((item, i) => (
        <ChapterCard
          item={item}
          itemPosition={i}
          key={`${item.title}-${i}`}
          isReadOnly={isReadOnlyUser}
          handleDelete={() => {
            setSelectedItemPosition(i);
            setShowDeleteItemModal(true);
          }}
          handleEdit={() => {
            setSelectedItem(item);
            setSelectedItemPosition(i);
            setShowEditChapterModal(true);
          }}
          onCreateClip={() => createClipHandler(item, i)}
        />
      ))}
      <DeleteIndexItemModal
        showModal={showDeleteItemModal}
        onCancel={() => setShowDeleteItemModal(false)}
        onDelete={onDelete}
      />
      <EditChapterModal
        showModal={showEditChapterModal}
        onCancel={() => setShowEditChapterModal(false)}
        item={selectedItem}
        onEdit={onEdit}
      />
      {showModal && (
        <ClipModal
          showModal={showModal}
          isCreating={true}
          onClose={() => {
            dispatch(updateCloseModal());
            setShowModal(false);
          }}
          onSave={createClipCall}
        />
      )}
    </div>
  );
};

export default Metadata;
